import React from "react";
import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/solid";
import { useAnimation } from "../hooks/useAnimate";
import Section from "../components/layout/Section";
import { TitleHeading } from "../components/typography/TitleHeading";
import BodyText from "./typography/BodyText";
import { twMerge } from "tailwind-merge";
import { Theme, useThemed } from "../hooks/useTheme";

interface FaqProps {
  theme?: Theme;
  tag: string;
  bgTagText?: string;
  bgTag?: boolean;
  title: string;
  description?: string;
  firstOpen?: boolean;
  faqs: {
    question: string;
    answer: string;
  }[];
  chunked?: boolean;
}

export const Faq = (props: FaqProps) => {
  const { id, animate } = useAnimation("faq");
  const themed = useThemed(props.theme ?? "white");

  return (
    <Section id={id} theme={props.theme}>
      <div {...animate("fade-down")}>
        <TitleHeading
          tag={props.tag}
          bgTagText={props.bgTagText}
          title={props.title}
          center
          bgTag={props.bgTag ?? true}
          theme={props.theme}
        />
        {props.description && (
          <BodyText
            className="max-w-4xl mt-4 mx-auto z-50"
            center
            dark={props.theme === "dark" || props.theme === "indigo"}
          >
            {props.description}
          </BodyText>
        )}
      </div>
      <div className="w-full my-12">
        <dl
          className={twMerge(
            themed({
              white: "divide-gray-200 dark:divide-gray-800",
              dark: "divide-gray-700",
              gray: "divide-gray-300 dark:divide-gray-700",
              indigo: "divide-indigo-700",
            }),
          )}
        >
          {props.faqs.map((faq, i) => (
            <Disclosure
              as="div"
              id={`faq-${i}`}
              key={`faq-${i}`}
              defaultOpen={props.firstOpen === false ? undefined : i == 0}
              className={twMerge(
                (i + 1) % 6 === 0 && props.chunked && "mb-24",
                i !== 0 && (!props.chunked || i % 6 != 0) && "border-t",
              )}
            >
              {({ open }) => (
                <>
                  <dt>
                    <Disclosure.Button
                      className={twMerge(
                        "text-left w-full flex justify-between items-center cursor-pointer group py-3",
                      )}
                    >
                      <div {...animate("fade-down", i * 100)}>
                        <h5
                          className={twMerge(
                            "text-2xl font-semibold",
                            themed({
                              white:
                                "text-gray-900 group-hover:text-gray-700 dark:text-gray-100 dark:group-hover:text-gray-300",
                              dark: "text-gray-100 group-hover:text-gray-300",
                              gray: "text-gray-900 group-hover:text-gray-700 dark:text-gray-100 dark:group-hover:text-gray-300",
                              indigo: "text-gray-100 group-hover:text-gray-300",
                            }),
                          )}
                          dangerouslySetInnerHTML={{ __html: faq.question }}
                        />
                      </div>
                      <span
                        {...animate("fade-left", i * 100)}
                        className="h-10 flex items-center text-primary ml-2"
                      >
                        <ChevronDownIcon
                          className={`${
                            open ? "-rotate-180" : "rotate-0"
                          } h-8 w-8 transform`}
                          aria-hidden="true"
                        />
                      </span>
                    </Disclosure.Button>
                  </dt>
                  <Disclosure.Panel as="dd" className=" mb-10 md:pl-6 xl:pl-12">
                    <BodyText
                      className="max-w-4xl"
                      dark={props.theme === "dark" || props.theme === "indigo"}
                    >
                      <span dangerouslySetInnerHTML={{ __html: faq.answer }} />
                    </BodyText>
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          ))}
        </dl>
      </div>
    </Section>
  );
};
